import React, { CSSProperties, useEffect, useLayoutEffect, useRef } from 'react'
import { createPopper } from '@popperjs/core'
import type { StrictModifiers, Instance } from '@popperjs/core'
import Portal from '../Portal'

export enum Placement {
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end'
}

interface PopperProps {
  open: boolean
  style?: CSSProperties
  modifiers: StrictModifiers[]
  placement: Placement
  targetRef?: HTMLElement
  arrowRef?: HTMLDivElement | null
  children: React.ReactElement<React.PropsWithChildren<{}>>
  transition?: boolean
}
export const disableMainAxis: StrictModifiers = {
  name: 'preventOverflow',
  options: {
    mainAxis: false
  }
}

export default function Popper({
  open,
  style,
  modifiers = [],
  placement = Placement.Bottom,
  targetRef,
  arrowRef,
  children,
  transition = false
}: PopperProps) {
  const popperRef = useRef<HTMLElement | HTMLDivElement>(null)
  const popperInstanceRef = useRef<Instance>(null)
  const handleOpen = React.useCallback(() => {
    if (!popperRef.current || !targetRef || !open) {
      return
    }
    popperInstanceRef.current?.destroy()
    const popper = createPopper(targetRef, popperRef.current, {
      placement,
      modifiers: [...modifiers, { name: 'arrow', options: { enabled: Boolean(arrowRef), element: arrowRef } }]
    })
    ;(popperInstanceRef as React.MutableRefObject<{}>).current = popper
  }, [arrowRef, modifiers, open, placement, targetRef])

  const handleRef = React.useCallback(
    (node: any) => {
      ;(popperRef as React.MutableRefObject<{}>).current = node
      handleOpen()
    },
    [handleOpen]
  )

  useLayoutEffect(() => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.forceUpdate()
    }
  }, [arrowRef, targetRef, open])
  useEffect(() => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.forceUpdate()
    }
  }, [popperRef.current, targetRef, open])

  useEffect(() => {
    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy()
      }
    }
  }, [])
  if (!open) {
    return null
  }
  const childrenProps = {
    ...children?.props,
    ref: handleRef,
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      display: !open && !transition ? 'none' : null,
      zIndex: 10000,
      ...style
    }
  }
  return <Portal>{React.cloneElement(children, childrenProps)}</Portal>
}
